import React, { useEffect, useState } from 'react';
import { Source, Layer, Popup, useMap } from 'react-map-gl';
import * as turf from '@turf/turf';
import { VALID_ROUTE_FIXES } from '../utils/utils';
import styled, { keyframes } from 'styled-components';
import {
  FaPlaneDeparture,
  FaPlaneArrival,
  FaClipboard,
  FaExclamationTriangle,
  FaExternalLinkAlt,
  FaArrowUp,
  FaTachometerAlt,
} from 'react-icons/fa';
import Stats from './Stats';
import FlightBoard from './FlightBoard';
import '../App.css';

const TRAFFIC_URL = 'https://data.vatsim.net/v3/vatsim-data.json';
const REFRESH_INTERVAL = 15000;

const airports = [
  {
    code: 'EFRO',
    icon: '/img/tfc-efro.png',
    name: 'EFRO',
    coords: [25.8476, 66.5608],
  },
  {
    code: 'ENTC',
    icon: '/img/tfc-entc.png',
    name: 'ENTC',
    coords: [18.9113, 69.682],
  },
  {
    code: 'ESNQ',
    icon: '/img/tfc-esnq.png',
    name: 'ESNQ',
    coords: [20.3718, 67.855],
  },
];

const StyledPopup = styled(Popup)`
  .mapboxgl-popup-content {
    background: #1e1e2f;
    color: #fff;
    font-family: 'Roboto', sans-serif;
    border-radius: 12px;
    padding: 12px;
    width: 220px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
  }

  a {
    color: #1db954;
    text-decoration: none;
    font-weight: 500;
    &:hover {
      text-decoration: underline;
    }
  }

  .popup-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
    font-size: 1rem;
    font-weight: 700;
    padding-left: 6px;
  }

  .popup-header .route-warning {
    color: #ffae00;
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 0.75rem;
  }

  .popup-card {
    width: 91px;
    display: flex;
    align-items: center;
    gap: 8px;
    background: #2a2a40;
    padding: 6px 8px;
    border-radius: 6px;
    cursor: pointer;
    font-size: 0.9rem;
  }

  .popup-card svg {
    color: #00d1d1;
  }

  .flight-info {
    display: flex;
    justify-content: space-between;
    margin-bottom: 8px;
    font-size: 0.85rem;
    span {
      background: #2a2a40;
      padding: 4px 8px;
      border-radius: 6px;
      font-weight: 500;
    }
  }

  .actions {
    display: flex;
    justify-content: space-between;
    a.copy-route {
      width: 91px;
      display: flex;
      align-items: center;
      gap: 4px;
      padding: 0;
      cursor: pointer;
      font-size: 0.8rem;
    }
  }
`;

const Traffic = () => {
  const { current: map } = useMap();
  const [trafficData, setTrafficData] = useState([]);
  const [popupInfo, setPopupInfo] = useState(null);
  const [airportToggles, setAirportToggles] = useState({
    EFRO: true,
    ESNQ: true,
    ENTC: true,
  });
  const [trafficCount, setTrafficCount] = useState({
    EFRO: 0,
    ESNQ: 0,
    ENTC: 0,
  });
  const [nextHourTraffic, setNextHourTraffic] = useState({
    EFRO: 0,
    ESNQ: 0,
    ENTC: 0,
  });
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch traffic data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch(TRAFFIC_URL);
        const data = await res.json();
        const filtered = data.pilots.filter(
          (p) =>
            p.flight_plan &&
            airports.some((a) => a.code === p.flight_plan.arrival),
        );
        setTrafficData(filtered);

        const calcNextHour = (code, coords) =>
          filtered.filter((p) => {
            const dist = turf.distance([p.longitude, p.latitude], coords, {
              units: 'nauticalmiles',
            });
            return (
              dist >= 10 &&
              dist <= 420 &&
              p.groundspeed > 10 &&
              p.flight_plan.arrival === code
            );
          }).length;

        setTrafficCount({
          EFRO: filtered.filter((p) => p.flight_plan.arrival === 'EFRO').length,
          ESNQ: filtered.filter((p) => p.flight_plan.arrival === 'ESNQ').length,
          ENTC: filtered.filter((p) => p.flight_plan.arrival === 'ENTC').length,
        });

        setNextHourTraffic({
          EFRO: calcNextHour(
            'EFRO',
            airports.find((a) => a.code === 'EFRO').coords,
          ),
          ESNQ: calcNextHour(
            'ESNQ',
            airports.find((a) => a.code === 'ESNQ').coords,
          ),
          ENTC: calcNextHour(
            'ENTC',
            airports.find((a) => a.code === 'ENTC').coords,
          ),
        });
      } catch (err) {
        console.error('Error fetching traffic data:', err);
      }
    };

    fetchData();
    const interval = setInterval(fetchData, REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, []);

  // Aircraft click popup
  useEffect(() => {
    if (!map) return;

    const handleClick = (e) => {
      const features = map.queryRenderedFeatures(e.point, {
        layers: ['traffic-icons'],
      });
      if (!features.length) return;

      const f = features[0];
      const {
        altitude,
        groundspeed,
        heading,
        callsign,
        departure,
        arrival,
        route,
        routeIsValid,
      } = f.properties;
      const flightLevel =
        altitude >= 6000
          ? 'F' + Math.round(altitude / 100)
          : 'A' +
            Math.round(altitude / 100)
              .toString()
              .padStart(3, '0');
      const skyvectorLink = `https://skyvector.com/?fpl=${departure}%20${route.split(' ').join('%20')}%20${arrival}`;

      // Reset popup first, then set new one in the next tick
      setPopupInfo(null);
      setTimeout(() => {
        setPopupInfo({
          coordinates: f.geometry.coordinates,
          callsign,
          departure,
          arrival,
          groundspeed,
          flightLevel,
          route,
          routeIsValid,
          skyvectorLink,
        });
      }, 0);
    };

    map.on('click', handleClick);
    map.on(
      'mouseenter',
      'traffic-icons',
      () => (map.getCanvas().style.cursor = 'pointer'),
    );
    map.on(
      'mouseleave',
      'traffic-icons',
      () => (map.getCanvas().style.cursor = ''),
    );

    return () => {
      map.off('click', handleClick);
      map.off('mouseenter', 'traffic-icons');
      map.off('mouseleave', 'traffic-icons');
    };
  }, [map, trafficData]);

  const toggleAirport = (code) =>
    setAirportToggles({ ...airportToggles, [code]: !airportToggles[code] });

  const handleSearch = () => {
    if (!searchTerm) return;
    const aircraft = trafficData.find(
      (p) => p.callsign.toUpperCase() === searchTerm,
    );
    if (!aircraft) return alert('Call sign not found');

    map.flyTo({
      center: [aircraft.longitude, aircraft.latitude],
      zoom: 6,
      speed: 1.6,
    });
    const route = aircraft.flight_plan.route;
    const flightLevel =
      aircraft.altitude >= 6000
        ? 'F' + Math.round(aircraft.altitude / 100)
        : 'A' +
          Math.round(aircraft.altitude / 100)
            .toString()
            .padStart(3, '0');
    const skyvectorLink = `https://skyvector.com/?fpl=${aircraft.flight_plan.departure}%20${route.split(' ').join('%20')}%20${aircraft.flight_plan.arrival}`;

    setPopupInfo({
      coordinates: [aircraft.longitude, aircraft.latitude],
      callsign: aircraft.callsign,
      departure: aircraft.flight_plan.departure,
      arrival: aircraft.flight_plan.arrival,
      groundspeed: aircraft.groundspeed,
      flightLevel,
      route,
      routeIsValid: Object.values(VALID_ROUTE_FIXES).some((f) =>
        f.every((fix) => route.includes(fix)),
      ),
      skyvectorLink,
    });
  };

  const trafficGeoJson = {
    type: 'FeatureCollection',
    features: trafficData
      .filter((p) => airportToggles[p.flight_plan.arrival])
      .map((p) => {
        const routeIsValid = Object.values(VALID_ROUTE_FIXES).some((f) =>
          f.every((fix) => p.flight_plan.route.includes(fix)),
        );
        return {
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [p.longitude, p.latitude] },
          properties: {
            id: p.cid,
            callsign: p.callsign,
            altitude: p.altitude,
            groundspeed: p.groundspeed,
            heading: p.heading,
            departure: p.flight_plan.departure,
            arrival: p.flight_plan.arrival,
            route: p.flight_plan.route,
            routeIsValid,
          },
        };
      }),
  };

  return (
    <>
      {/* Top Menu */}
      <div className="fixed top-0 left-0 w-full z-20 bg-gradient-to-r from-blue-950 to-gray-950 shadow-md p-2 flex flex-col md:flex-row justify-between items-center md:gap-4 gap-2">
        <div className="flex gap-2">
          {airports.map((a) => (
            <button
              key={a.code}
              onClick={() => toggleAirport(a.code)}
              className={`flex items-center whitespace-nowrap gap-2 px-2 py-1 text-sm font-medium shadow-md border border-gray-700 transition ${airportToggles[a.code] ? 'bg-gray-300/10 text-white' : 'bg-gray-700/10 text-gray-300'}`}
            >
              <img src={a.icon} className="w-6 h-6 rotate-45" alt={a.name} />
              <span className="text-green-400 font-bold">
                {trafficCount[a.code]}
              </span>{' '}
              <span className="text-xs text-gray-400">
                ({nextHourTraffic[a.code]} /h)
              </span>
            </button>
          ))}
        </div>

        <h1
          className="cursor-pointer text-xl items-center font-extrabold leading-[1] hidden md:block"
          onClick={() =>
            window.open('https://fss.vatsim-scandinavia.org/', '_blank')
          }
        >
          Fly and See{' '}
          <span className="bg-clip-text text-transparent bg-gradient-to-r from-red-500 to-pink-700">
            Santa
          </span>
        </h1>

        <div className="flex items-center gap-2">
          <input
            type="text"
            placeholder="Search callsign"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value.toUpperCase())}
            className="px-2 py-1 max-w-[130px] border border-gray-700 bg-gray-300/10 text-white placeholder-gray-500 text-sm focus:outline-none focus:ring-1 focus:ring-blue-800"
          />
          <button
            onClick={handleSearch}
            className="px-4 py-1 bg-blue-700/40 border border-gray-700 text-white font-semibold hover:bg-blue-700/50 transition"
          >
            Search
          </button>
        </div>
      </div>

      {/* Traffic Layer */}
      <Source id="traffic" type="geojson" data={trafficGeoJson}>
        {/* Icons */}
        <Layer
          id="traffic-icons"
          type="symbol"
          layout={{
            'icon-image': [
              'case',
              ['==', ['get', 'arrival'], 'EFRO'],
              'tfc-efro',
              ['==', ['get', 'arrival'], 'ESNQ'],
              'tfc-esnq',
              ['==', ['get', 'arrival'], 'ENTC'],
              'tfc-entc',
              'default-icon',
            ],
            'icon-rotation-alignment': 'map',
            'icon-rotate': ['get', 'heading'],
            'icon-size': 0.3,
            'icon-allow-overlap': true, // allow icons to overlap
          }}
        />

        {/* Labels */}
        <Layer
          id="traffic-labels"
          type="symbol"
          layout={{
            'text-field': ['get', 'callsign'],
            'text-size': 10,
            'text-anchor': 'left',
            'text-offset': [1, 0],
            'text-allow-overlap': false,
            'text-ignore-placement': false,
            'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
          }}
          paint={{
            'text-color': [
              'case',
              ['==', ['get', 'routeIsValid'], false],
              '#ffae00',
              '#ccc',
            ],
            'text-halo-color': 'black',
            'text-halo-width': 1,
          }}
          minzoom={3}
        />
      </Source>

      {/* Popup */}
      {popupInfo && (
        <StyledPopup
          longitude={popupInfo.coordinates[0]}
          latitude={popupInfo.coordinates[1]}
          closeOnClick={true}
          onClose={() => setPopupInfo(null)}
          anchor="top"
          closeButton={false}
        >
          <div className="popup-header">
            <span>{popupInfo.callsign}</span>
            {!popupInfo.routeIsValid && (
              <span className="route-warning">
                <FaExclamationTriangle /> Verify Route
              </span>
            )}
            <button
              onClick={() => setPopupInfo(null)}
              style={{
                background: '#d63c3c',
                border: 'none',
                borderRadius: '50%',
                width: '22px',
                height: '22px',
                color: '#fff',
                fontWeight: 'bold',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0,
              }}
            >
              ×
            </button>
          </div>
          <div className="flight-info">
            <span className="popup-card">
              <FaPlaneDeparture /> {popupInfo.departure}
            </span>
            <span className="popup-card">
              <FaPlaneArrival /> {popupInfo.arrival}
            </span>
          </div>
          <div className="flight-info">
            <span className="popup-card">
              <FaArrowUp /> {popupInfo.flightLevel}
            </span>
            <span className="popup-card">
              <FaTachometerAlt /> N
              {popupInfo.groundspeed.toString().padStart(4, '0')}
            </span>
          </div>
          <div className="actions">
            <a
              className="copy-route"
              href={popupInfo.skyvectorLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              <FaExternalLinkAlt /> SkyVector
            </a>
            <a
              className="copy-route"
              onClick={() =>
                navigator.clipboard.writeText(
                  `${popupInfo.departure} ${popupInfo.route} ${popupInfo.arrival}`,
                )
              }
            >
              <FaClipboard /> Copy Route
            </a>
          </div>
        </StyledPopup>
      )}
      <Stats trafficData={trafficData} />
      <FlightBoard trafficData={trafficData} />
    </>
  );
};

export default Traffic;
